import React from 'react';
import { IStore } from 'Store/.';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { injectIntl, defineMessages, FormattedMessage, intlShape } from 'react-intl';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { Dispatch, bindActionCreators } from 'redux';
import { getOrderRequest } from 'Store/Order/selectors';
import { getPerformance, getSeatTypes } from 'Components/OrderItem/Redux/selectors';
import PickSeatAutomatic from 'Components/PickSeatAutomatic/PickSeatAutomatic';

import { 
  setIsManualReservation, 
  setSeatRequests, 
  setIsScatteredSeats,
} from 'Store/Order/actions';

interface IProps {
  onPickSeatsManually: () => void;
  onPickSeatsAutomatically: () => void;
}

interface IPropsInternal {
  orderRequest: Julius.Website.OrderRequestViewModel;
  performance: Julius.Website.PerformanceOverviewViewModel;
  setIsManualReservation: (isManualReservation: boolean) => void;
  setIsScatteredSeats: (isScatteredSeats: boolean) => void;
  setSeatRequests: (seatRequests: Julius.Website.SeatRequestViewModel[]) => void;
  seatTypes: Julius.Website.PerformanceSeatTypeViewModel[];
}

interface IState {
  sectionID: number;
  isShowingEmptyWarning: boolean;
  isShowingCategoryWarning: boolean;
}

type Props = IProps & IPropsInternal & RouteComponentProps<{ orderItemID: any }>;

class PickSeatAutomaticContainer extends React.PureComponent<Props, IState> {
  constructor(props: Props) {
    super(props);

    this.state = {
      sectionID: 0,
      isShowingEmptyWarning: false,
      isShowingCategoryWarning: false,
    }
  }

  componentDidMount() {
    this.props.setIsManualReservation(false);
  }

  componentWillReceiveProps(nextProps: Props) {
    const { isShowingEmptyWarning, isShowingCategoryWarning } = this.state;

    const shouldReValidate = 
      nextProps.orderRequest !== this.props.orderRequest &&
      (isShowingEmptyWarning || isShowingCategoryWarning);
    
    if (shouldReValidate) {
      const { seatRequests } = nextProps.orderRequest;
      this.validateSeatSelection(seatRequests);
    }
  }

  onSeatSelection = (selection: Julius.Website.SeatRequestViewModel) => {
    const newDiscountID = selection.discountID;
    const { seatRequests } = this.props.orderRequest;

    const newSeatRequests = seatRequests.filter(
      s => !(s.discountID === newDiscountID));

    newSeatRequests.push(selection);
    this.props.setSeatRequests(newSeatRequests);
  }

  onScatteredSeatsChange = (isChecked: boolean) => {
    this.props.setIsScatteredSeats(isChecked);
  }

  onSectionSelection = (sectionID: number) => {
    this.setState({ sectionID });
  }

  onPickManually = () => {
    this.props.onPickSeatsManually();
  }

  onPickAutomatically = () => {
    const { seatRequests } = this.props.orderRequest;
    const isValid = this.validateSeatSelection(seatRequests);

    if (isValid) {
      const newSeatRequests = seatRequests.map(
          request => {
            request.sectionID = this.state.sectionID;
            return request;
          });

      this.props.setSeatRequests(newSeatRequests);
      this.props.onPickSeatsAutomatically();
    }
  }

  validateSeatSelection = (seatRequests: Julius.Website.SeatRequestViewModel[]) => {
    const isEmpty = this.countSeats(seatRequests) <= 0;
    const isDifferentCategories = this.isDifferentPriceCategories(seatRequests);
    const { isShowingEmptyWarning, isShowingCategoryWarning } = this.state;

    if (
      (isEmpty !== isShowingEmptyWarning) ||
      (isDifferentCategories !== isShowingCategoryWarning) 
    ) {
      this.setState({
        isShowingEmptyWarning: isEmpty,
        isShowingCategoryWarning: isDifferentCategories,
      });
    }

    return !(isEmpty || isDifferentCategories);
  }

  countSeats = (seatRequests: Julius.Website.SeatRequestViewModel[]) => {
    return seatRequests.reduce(
      (total, request) => total + request.numberOfSeats, 0);
  }

  isDifferentPriceCategories = (seatRequests: Julius.Website.SeatRequestViewModel[]) => {
    for (let i = 1; i < seatRequests.length; i++) {
      if (seatRequests[i].priceCategoryID !== seatRequests[i-1].priceCategoryID) {
        return true;
      }
    }
    return false;
  }

  render() {

    const { performance, orderRequest: { seatRequests } } = this.props;

    if (!performance) {
      return null;
    }

    const maxNbrSeatsPromotion = (): number => {
      const seatTypeMax = this.props.seatTypes.reduce((prev, current) => (
        (prev.maxNbrSeatsPromotion > current.maxNbrSeatsPromotion) ? prev : current
        )).maxNbrSeatsPromotion;
        if (seatTypeMax === 0) {
          return performance.maxNumberOfSeats;
        }
        return seatTypeMax;
      }

    const isMaxNumberOfSeats =
      this.countSeats(seatRequests) >= maxNbrSeatsPromotion();
      
    return (
      <PickSeatAutomatic 
        isNumberedSeats={ this.props.performance.isNumberedSeats }
        isScatteredSeats={ this.props.orderRequest.isScatteredSeats }
        isTimeSlot={ this.props.performance.isTimeSlot }
        isMaxNumberOfSeats={ isMaxNumberOfSeats }
        maxNumberOfSeats={ maxNbrSeatsPromotion() }
        isShowingEmptyWarning={ this.state.isShowingEmptyWarning }
        isShowingCategoryWarning={ this.state.isShowingCategoryWarning }
        selectedSectionID={ this.state.sectionID }
        selectedSeats={ this.props.orderRequest.seatRequests }
        seatTypes={ this.props.performance.seatTypes }
        onSectionSelection={ this.onSectionSelection }
        onSeatSelection={ this.onSeatSelection }
        onScatteredSeatsChange={ this.onScatteredSeatsChange }
        onPickManually={ this.onPickManually }
        onPickAutomatically={ this.onPickAutomatically }
        allowManualReservation={ this.props.performance.allowManualReservation }
      />
    );
  }
}

const mapStateToProps = (state: IStore) => {
  return {
    performance: getPerformance(state),
    orderRequest: getOrderRequest(state),
    seatTypes: getSeatTypes(state),
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => {
  return bindActionCreators({
    setSeatRequests,
    setIsScatteredSeats,
    setIsManualReservation,
  }, dispatch);
};

export default compose<{}, {}>(
  withRouter,
  injectIntl,
  connect(mapStateToProps, mapDispatchToProps),
)(PickSeatAutomaticContainer);


// WEBPACK FOOTER //
// ./src/components/PickSeatAutomatic/PickSeatAutomaticContainer.tsx